import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BfAuthService } from '@brafton/skynet-angular-security-service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { HOME_ROUTE, LOGIN_ROUTE } from '../../core/skynet-auth.config';

/**
 * True when the URL fragment is an Auth0 redirect callback, i.e. it carries
 * `access_token` / `id_token` / `error`. Read straight from `location.hash`
 * rather than router state, since the raw fragment is the thing that matters.
 */
function isAuth0Callback(): boolean {
  return /[#&](access_token|id_token|error)=/.test(globalThis.location?.hash ?? '');
}

/**
 * Landing page for the app root, and the target Auth0 redirects back to.
 *
 * This route exists, and is deliberately NOT behind {@link authGuard}, because
 * Auth0 returns to the app ORIGIN with the tokens in the URL fragment. If the
 * root were guarded, the guard would run before Auth0 Lock had parsed that
 * fragment, find no session, and cancel the navigation — and a cancelled
 * navigation makes the router reset the URL, DISCARDING the fragment. Lock could
 * then never complete and the user would be stranded on "Signing in…". A route
 * that actually activates keeps the fragment intact (verified: the fragment
 * survives activation but not cancellation).
 *
 * So on a callback this page does nothing but wait: the root component has
 * already registered `handleAuthentication()`, and once Lock reports success the
 * library itself stores the tokens and navigates on — to the `ru=` return URL in
 * Auth0's `state`, else `HomePageURL`.
 *
 * The library's own demo app gets this for free by routing '' to its login
 * component rather than to a guarded shell.
 */
@Component({
  selector: 'app-auth-callback-page',
  imports: [MatProgressSpinnerModule],
  templateUrl: './auth-callback-page.html',
  styleUrl: './auth-callback-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthCallbackPage implements OnInit {
  private readonly auth = inject(BfAuthService);
  private readonly router = inject(Router);

  /** Drives the "Signing you in…" message while Lock finishes the callback. */
  protected readonly completingSignIn = signal(false);

  ngOnInit(): void {
    if (isAuth0Callback()) {
      // Hold here and let the library's Lock listener finish and navigate.
      this.completingSignIn.set(true);
      return;
    }

    void this.router.navigateByUrl(this.auth.authenticated() ? HOME_ROUTE : LOGIN_ROUTE);
  }
}
